import "aos/dist/aos.css";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { LuCalendarDays, LuMapPin, LuTicket } from "react-icons/lu";
import { Link } from "react-router-dom";
import { buildMeetingRegisterPath } from "./attendeeUrlUtils";
import MeetingAgendaTable from "./MeetingAgendaTable";
import "./meeting.scss";
import { formatAmount, formatPricingDate, getPricingRows, getTierLabel } from "./meetingPricing";

const ASSET_BASE_URL = "https://msid-asset.s3.ap-northeast-2.amazonaws.com";
const ORGANIZATION_CATEGORY_KEYS = ['байгууллага', 'organization', 'organizations'];
const PRODUCT_CATEGORY_KEYS = ['бүтээгдэхүүн', 'product', 'products'];

const getAssetUrl = (fileUrl) => {
  if (!fileUrl) return "";
  if (/^https?:\/\//i.test(fileUrl)) return fileUrl;
  return `${ASSET_BASE_URL}/${fileUrl}`;
};

const AnnualMeeting = ({ data, item, language, showRegistration = true, contentView = null }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [currentSponsorSlide1, setCurrentSponsorSlide1] = useState(0);
  const [currentSponsorSlide2, setCurrentSponsorSlide2] = useState(0);
  const sliderRef = useRef(null);
  const sponsorSliderRef1 = useRef(null);
  const sponsorSliderRef2 = useRef(null);

  const getLocalizedField = (source, field) => {
    if (!source) return "";

    if (language) {
      const englishValue = source[`${field}_en`];
      if (typeof englishValue === "string" && englishValue.trim()) return englishValue;
    }

    const value = source[field];
    return typeof value === "string" ? value : value || "";
  };

  const getDisplayName = (source) => (
    getLocalizedField(source, "name") ||
    getLocalizedField(source, "title")
  );

  const handleClick = (url) => {
    const normalizedUrl = typeof url === "string" ? url.trim() : url;
    if (normalizedUrl) {
      window.open(normalizedUrl, "_blank");
    }
  };

  const getItemsPerView = () => {
    const width = window.innerWidth;
    if (width <= 600) return 1;
    if (width <= 768) return 2;
    return 3;
  };

  const getTotalSlides = () => {
    if (!item?.speakers) return 0;
    const itemsPerView = getItemsPerView();
    return Math.ceil(item.speakers.length / itemsPerView);
  };

  const goToSlide = (slideIndex) => {
    if (!sliderRef.current || !item?.speakers) return;
    
    const itemsPerView = getItemsPerView();
    const itemWidth = sliderRef.current.scrollWidth / item.speakers.length;
    const scrollPosition = slideIndex * itemsPerView * itemWidth;
    
    sliderRef.current.scrollTo({
      left: scrollPosition,
      behavior: 'smooth'
    });
    setCurrentSlide(slideIndex);
  };

  useEffect(() => {
    const updateSlideOnScroll = () => {
      if (!sliderRef.current || !item?.speakers) return;
      
      const itemsPerView = getItemsPerView();
      const scrollLeft = sliderRef.current.scrollLeft;
      const itemWidth = sliderRef.current.scrollWidth / item.speakers.length;
      const totalSlides = Math.ceil(item.speakers.length / itemsPerView);
      const newSlide = Math.round(scrollLeft / (itemWidth * itemsPerView));
      
      setCurrentSlide(Math.min(newSlide, totalSlides - 1));
    };

    const slider = sliderRef.current;
    if (slider) {
      slider.addEventListener('scroll', updateSlideOnScroll);
      window.addEventListener('resize', updateSlideOnScroll);
      
      return () => {
        if (slider) {
          slider.removeEventListener('scroll', updateSlideOnScroll);
        }
        window.removeEventListener('resize', updateSlideOnScroll);
      };
    }
  }, [item?.speakers]);

  const sortAgendaRows = (rows) => {
    if (!rows) return [];
    
    return rows.slice().sort((a, b) => {
      // Headers first
      if (a.header && !b.header) return -1;
      if (!a.header && b.header) return 1;
      
      // Then sort by col1 in ascending order
      const timeA = a.col1 || '';
      const timeB = b.col1 || '';
      return timeA.localeCompare(timeB);
    });
  };

  // CSS handles responsive layout - always use 2 for slider calculations
  const SPONSORS_PER_VIEW = 2;

  const getSponsorsByCategory = useCallback((category) => {
    if (!item?.sponsors) return [];
    const categories = Array.isArray(category) ? category : [category];
    return item.sponsors.filter(s => categories.includes((s?.category || "").trim().toLowerCase()));
  }, [item?.sponsors]);

  const getTotalSponsorSlides = useCallback((category) => {
    const sponsors = getSponsorsByCategory(category);
    return Math.ceil(sponsors.length / SPONSORS_PER_VIEW);
  }, [getSponsorsByCategory]);

  const pricingRows = getPricingRows(item);
  const hasEarlyBirdPricing = pricingRows.some((row) => Number(row.discount_amount) > 0);
  const earlyBirdPricingEndDates = Array.from(new Set(pricingRows
    .filter((row) => Number(row.discount_amount) > 0 && Number(row.original_amount) > Number(row.amount))
    .map((row) => formatPricingDate(row.discount_ends_at, language))
    .filter(Boolean)));
  const meetingTitle = language
    ? (item?.title_en && item.title_en.trim() ? item.title_en : item?.title)
    : item?.title;
  const meetingDescription = language
    ? (item?.description_en && item.description_en.trim() ? item.description_en : item?.description)
    : item?.description;
  const meetingDate = item?.dt
    ? new Date(item.dt).toLocaleString(language ? "en-US" : "mn-MN", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    })
    : "";
  const agendaRows = sortAgendaRows(item?.rows);
  const speakers = item?.speakers || [];
  const organizationSponsors = getSponsorsByCategory(ORGANIZATION_CATEGORY_KEYS);
  const productSponsors = getSponsorsByCategory(PRODUCT_CATEGORY_KEYS);
  const hasSponsors = organizationSponsors.length > 0 || productSponsors.length > 0;
  const usesSectionViews = contentView !== null;
  const showOverview = !usesSectionViews || contentView === "overview";
  const showAgenda = !usesSectionViews ? agendaRows.length > 0 : contentView === "agenda" && agendaRows.length > 0;
  const showSpeakers = !usesSectionViews ? speakers.length > 0 : contentView === "speakers" && speakers.length > 0;

  const goToSponsorSlide = (slideIndex, sliderRef, setSlideState) => {
    if (!sliderRef.current) return;
    
    const sponsors = sliderRef.current.children;
    if (sponsors.length === 0) return;
    
    const itemWidth = sponsors[0].offsetWidth + 20; // item width + gap
    const scrollPosition = slideIndex * SPONSORS_PER_VIEW * itemWidth;
    
    sliderRef.current.scrollTo({
      left: scrollPosition,
      behavior: 'smooth'
    });
    setSlideState(slideIndex);
  };

  // useEffect for first sponsor slider (байгууллага)
  useEffect(() => {
    const updateSponsorSlideOnScroll1 = () => {
      if (!sponsorSliderRef1.current) return;
      
      const sponsors = getSponsorsByCategory(ORGANIZATION_CATEGORY_KEYS);
      if (sponsors.length === 0) return;
      
      const scrollLeft = sponsorSliderRef1.current.scrollLeft;
      const itemWidth = sponsorSliderRef1.current.children[0]?.offsetWidth + 20 || 200;
      const totalSlides = getTotalSponsorSlides(ORGANIZATION_CATEGORY_KEYS);
      const newSlide = Math.round(scrollLeft / (itemWidth * SPONSORS_PER_VIEW));
      
      setCurrentSponsorSlide1(Math.min(newSlide, totalSlides - 1));
    };

    const sponsorSlider1 = sponsorSliderRef1.current;
    if (sponsorSlider1) {
      sponsorSlider1.addEventListener('scroll', updateSponsorSlideOnScroll1);
      window.addEventListener('resize', updateSponsorSlideOnScroll1);
      
      return () => {
        if (sponsorSlider1) {
          sponsorSlider1.removeEventListener('scroll', updateSponsorSlideOnScroll1);
        }
        window.removeEventListener('resize', updateSponsorSlideOnScroll1);
      };
    }
  }, [getSponsorsByCategory, getTotalSponsorSlides]);

  // useEffect for second sponsor slider (бүтээгдэхүүн)
  useEffect(() => {
    const updateSponsorSlideOnScroll2 = () => {
      if (!sponsorSliderRef2.current) return;
      
      const sponsors = getSponsorsByCategory(PRODUCT_CATEGORY_KEYS);
      if (sponsors.length === 0) return;
      
      const scrollLeft = sponsorSliderRef2.current.scrollLeft;
      const itemWidth = sponsorSliderRef2.current.children[0]?.offsetWidth + 20 || 200;
      const totalSlides = getTotalSponsorSlides(PRODUCT_CATEGORY_KEYS);
      const newSlide = Math.round(scrollLeft / (itemWidth * SPONSORS_PER_VIEW));
      
      setCurrentSponsorSlide2(Math.min(newSlide, totalSlides - 1));
    };

    const sponsorSlider2 = sponsorSliderRef2.current;
    if (sponsorSlider2) {
      sponsorSlider2.addEventListener('scroll', updateSponsorSlideOnScroll2);
      window.addEventListener('resize', updateSponsorSlideOnScroll2);
      
      return () => {
        if (sponsorSlider2) {
          sponsorSlider2.removeEventListener('scroll', updateSponsorSlideOnScroll2);
        }
        window.removeEventListener('resize', updateSponsorSlideOnScroll2);
      };
    }
  }, [getSponsorsByCategory, getTotalSponsorSlides]);

  return (
    <section className="meeting">
      {item ? (
        <>
          {showOverview ? (
        <>
          {showRegistration && usesSectionViews ? (
            <div className="meeting-register-banner">
              <Link
                className="meeting-register-link"
                to={buildMeetingRegisterPath({ meetingId: item?.id || "" })}
                target="_blank"
                rel="noopener noreferrer"
              >
                <LuTicket aria-hidden="true" />
                <span>{data.register}</span>
              </Link>
            </div>
          ) : null}
        <div key={"meeting-" + item.id} className="meeting-item">
          <div className="meeting-hero">
            <div className="meeting-img">
              <img
                src={getAssetUrl(item.fileUrl)}
                alt={meetingTitle || data.title}
              />
            </div>
            <div className="meeting-hero-copy">
              <div className="meeting-eyebrow">{data.title}</div>
              <h3>{meetingTitle}</h3>

              <div className="meeting-meta">
                {meetingDate ? (
                  <div className="meeting-meta-item">
                    <LuCalendarDays aria-hidden="true" />
                    <span>{meetingDate}</span>
                  </div>
                ) : null}
                {item.location ? (
                  <div className="meeting-meta-item">
                    <LuMapPin aria-hidden="true" />
                    <span>{item.location}</span>
                  </div>
                ) : null}
              </div>

              {meetingDescription ? (
                <div className="event-desc">{meetingDescription}</div>
              ) : null}
            </div>
          </div>

          <div className="event-info">
            {pricingRows.length > 0 ? (
              <section className="meeting-section meeting-fees-section">
                <div className="meeting-section-header">
                  <div>
                    <h4>
                      {hasEarlyBirdPricing
                        ? (language ? "Early bird prices" : "Early bird үнэ")
                        : (language ? "Registration fees" : "Бүртгэлийн төлбөр")}
                    </h4>
                    {earlyBirdPricingEndDates.length > 0 ? (
                      <div className="meeting-section-note">
                        {language ? "Ends on" : "Дуусах"}: {earlyBirdPricingEndDates.join(", ")}
                      </div>
                    ) : null}
                  </div>
                </div>
                <div className="meeting-pricing">
                  {pricingRows.map((row) => {
                    const hasActiveEarlyBirdDeal = Number(row.discount_amount) > 0
                      && Number(row.original_amount) > Number(row.amount);

                    return (
                      <div className={"pricing-card" + (hasActiveEarlyBirdDeal ? " has-early-bird" : "")} key={row.tier}>
                        <div className="pricing-type">{getTierLabel(row.tier, language)}</div>
                        {hasActiveEarlyBirdDeal ? (
                          <>
                            <div className="pricing-amount pricing-amount-early">
                              {formatAmount(row.amount, row.currency)}
                            </div>
                            <div className="pricing-original pricing-original-striked">
                              {formatAmount(row.original_amount, row.currency)}
                            </div>
                          </>
                        ) : (
                          <div className="pricing-amount">{formatAmount(row.amount, row.currency)}</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </section>
            ) : null}

            {usesSectionViews ? null : (
            agendaRows.length > 0 ? (
              <section className="meeting-section" id="meeting-agenda">
                <div className="meeting-section-header">
                  <h4>{data.agenda}</h4>
                </div>
                <MeetingAgendaTable rows={agendaRows} getLocalizedField={getLocalizedField} />
              </section>
            ) : null
            )}

            {usesSectionViews ? null : (
            speakers.length > 0 ? (
              <section className="meeting-section" id="meeting-speakers">
                <div className="meeting-section-header">
                  <h4>{data.presenters}</h4>
                </div>
                <div className="presenters-slider-container">
                  <div className="sub-section presenters" ref={sliderRef}>
                    {speakers.map((speaker, index) => {
                      const speakerName = getDisplayName(speaker) || data.presenters;
                      const speakerJob = getLocalizedField(speaker, "job");
                      const speakerCompany = getLocalizedField(speaker, "company");
                      const speakerDescription = getLocalizedField(speaker, "description");

                      return (
                        <div key={index} className="item">
                          <button
                            className="speaker-photo"
                            type="button"
                            onClick={() => handleClick(speaker.link)}
                            disabled={!speaker.link}
                            aria-label={speakerName}
                          >
                            <img
                              src={getAssetUrl(speaker.fileUrl)}
                              alt={speakerName}
                            />
                          </button>
                          <div className="speaker-content">
                            <div className="name">{speakerName}</div>
                            {speakerJob ? <div className="job">{speakerJob}</div> : null}
                            {speakerCompany ? <div className="company">{speakerCompany}</div> : null}
                            {speakerDescription ? <div className="description">{speakerDescription}</div> : null}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {getTotalSlides() > 1 && (
                    <div className="slider-dots">
                      {Array.from({ length: getTotalSlides() }, (_, index) => (
                        <button
                          key={index}
                          className={`slider-dot ${currentSlide === index ? 'active' : ''}`}
                          onClick={() => goToSlide(index)}
                          aria-label={`Go to slide ${index + 1}`}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </section>
            ) : null
            )}

            {hasSponsors ? (
              <section className="meeting-section">
                <div className="meeting-section-header">
                  <h4>{data.sponsors}</h4>
                </div>

                {organizationSponsors.length > 0 ? (
                  <div className="sponsors-slider-container">
                    <div className="sponsor-category">
                      {language ? "Organizations" : "Байгууллага"}
                    </div>
                    <div className="sponsors" ref={sponsorSliderRef1}>
                      {organizationSponsors.map((sponsor, index) => {
                        const sponsorName = getDisplayName(sponsor) || data.sponsors;

                        return (
                          <button
                            key={index}
                            className="item"
                            type="button"
                            onClick={() => handleClick(sponsor.link)}
                            disabled={!sponsor.link}
                            aria-label={sponsorName}
                          >
                            <img
                              src={getAssetUrl(sponsor.fileUrl)}
                              alt={sponsorName}
                            />
                          </button>
                        );
                      })}
                    </div>
                    {getTotalSponsorSlides(ORGANIZATION_CATEGORY_KEYS) > 1 && (
                      <div className="slider-dots">
                        {Array.from({ length: getTotalSponsorSlides(ORGANIZATION_CATEGORY_KEYS) }, (_, index) => (
                          <button
                            key={index}
                            className={`slider-dot ${currentSponsorSlide1 === index ? 'active' : ''}`}
                            onClick={() => goToSponsorSlide(index, sponsorSliderRef1, setCurrentSponsorSlide1)}
                            aria-label={`Go to slide ${index + 1}`}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                ) : null}

                {productSponsors.length > 0 ? (
                  <div className="sponsors-slider-container">
                    <div className="sponsor-category">
                      {language ? "Products" : "Бүтээгдэхүүн"}
                    </div>
                    <div className="sponsors" ref={sponsorSliderRef2}>
                      {productSponsors.map((sponsor, index) => {
                        const sponsorName = getDisplayName(sponsor) || data.sponsors;

                        return (
                          <button
                            key={index}
                            className="item"
                            type="button"
                            onClick={() => handleClick(sponsor.link)}
                            disabled={!sponsor.link}
                            aria-label={sponsorName}
                          >
                            <img
                              src={getAssetUrl(sponsor.fileUrl)}
                              alt={sponsorName}
                            />
                          </button>
                        );
                      })}
                    </div>
                    {getTotalSponsorSlides(PRODUCT_CATEGORY_KEYS) > 1 && (
                      <div className="slider-dots">
                        {Array.from({ length: getTotalSponsorSlides(PRODUCT_CATEGORY_KEYS) }, (_, index) => (
                          <button
                            key={index}
                            className={`slider-dot ${currentSponsorSlide2 === index ? 'active' : ''}`}
                            onClick={() => goToSponsorSlide(index, sponsorSliderRef2, setCurrentSponsorSlide2)}
                            aria-label={`Go to slide ${index + 1}`}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                ) : null}
              </section>
            ) : null}
          </div>
        </div>
        </>
          ) : null}

          {showAgenda ? (
            <div className="meeting-item">
              <div className="event-info">
                <section className="meeting-section">
                  <div className="meeting-section-header">
                    <h4>{data.agenda}</h4>
                  </div>
                  <MeetingAgendaTable rows={agendaRows} getLocalizedField={getLocalizedField} />
                </section>
              </div>
            </div>
          ) : null}

          {showSpeakers ? (
            <div className="meeting-item">
              <div className="event-info">
                <section className="meeting-section">
                  <div className="meeting-section-header">
                    <h4>{data.presenters}</h4>
                  </div>
                  <div className="presenters-slider-container">
                    <div className="sub-section presenters" ref={sliderRef}>
                      {speakers.map((speaker, index) => {
                        const speakerName = getDisplayName(speaker) || data.presenters;
                        const speakerJob = getLocalizedField(speaker, "job");
                        const speakerCompany = getLocalizedField(speaker, "company");
                        const speakerDescription = getLocalizedField(speaker, "description");

                        return (
                          <div key={index} className="item">
                            <button
                              className="speaker-photo"
                              type="button"
                              onClick={() => handleClick(speaker.link)}
                              disabled={!speaker.link}
                              aria-label={speakerName}
                            >
                              <img
                                src={getAssetUrl(speaker.fileUrl)}
                                alt={speakerName}
                              />
                            </button>
                            <div className="speaker-content">
                              <div className="name">{speakerName}</div>
                              {speakerJob ? <div className="job">{speakerJob}</div> : null}
                              {speakerCompany ? <div className="company">{speakerCompany}</div> : null}
                              {speakerDescription ? <div className="description">{speakerDescription}</div> : null}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    {getTotalSlides() > 1 && (
                      <div className="slider-dots">
                        {Array.from({ length: getTotalSlides() }, (_, index) => (
                          <button
                            key={index}
                            className={`slider-dot ${currentSlide === index ? 'active' : ''}`}
                            onClick={() => goToSlide(index)}
                            aria-label={`Go to slide ${index + 1}`}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                </section>
              </div>
            </div>
          ) : null}
        </>
      ) : (
        <div className="d-flex justify-content-center w-100">
          {data.noDataFound}
        </div>
      )}
    </section>
  );
};
export default AnnualMeeting;
